import { useEffect, useRef, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import _ from 'lodash'

import { Box, Flex } from '@chakra-ui/react'
import DsrHeader from '../components/DsrHeader'
import DsrGenericErrorModal from '../components/DsrGenericErrorModal'
import DsrDynamicModal from '../messages/DsrDynamicModal'
import RenderDraftDsrContent from './RenderDraftDsrContent'
import RenderPreviewDsrContent from "./RenderPreviewDsrContent"
import LaunchdeckButton from '../../global/components/helper-components/components/LaunchdeckButton';
import Header from '../../layout/components/header/components/Header'
import { TbDeviceDesktopQuestion } from "react-icons/tb";
import { redirectToNewPage } from '../../global/helpers/global-helpers'
import useLaunchdeckModal from '../../global/hooks/use-launchdeck-modal'


export const DsrEditDraftPage = ({
  dsrTitle,
  isLiveEdit,
  dsrPagesRedux,
  dsrTeamMembersRedux,
  showDsrModalRedux,
  userActionRequiredRedux,
  showDsrModalLoaderRedux,
  currentDsrId,
  fetchInitialDsrData,
  resetDsrFeedContent,
  resetDsrTitle,
  resetDsrSelectedDisplaySubpage,
  resetDsrPages,
  updateDsr,
  updateDsrReset,
  selectedDisplaySubpage,
  getDsrFeedContent,
  dsrFeedContent,
  dsrInvalidRequestRedux,
  isMobileView,
  duplicateDsrPage,
  isLockedRoom,
  lockedRoomMetadata,
  unlockRoom,
  hideOptionForInvalidLink,
}) => {
  const { dsrId } = useParams();
  const history = useHistory();

  const initRef = useRef(false);
  const lockedRoomRef = useRef(null);
  const timeoutRef = useRef(null); // Keep track of the timeout

  const { openLaunchdeckModal, closeLaunchdeckModal } = useLaunchdeckModal();

  let [dsrPagesLocalState, setDsrPagesLocalState] = useState(null)
  let [dsrHasUnsavedChanges, setDsrHasUnsavedChanges] = useState(false)
  let [modalTransferPayload, setModalTransferPayload] = useState('')
  let [isPagePreviewMode, setIsPagePreviewMode] = useState(false)
  let [contentIsUnavailable, setContentIsUnavailable] = useState(false)

  useEffect(() => {
    if (!initRef.current && dsrId !== 'new') {
      document.title = 'Launchdeck Digital Sales Room'
      document.description =
        'Create customised private digital sales rooms that let Sales & Purchasing teams agree on deals quickly'

      const requiredStatuses = isLiveEdit ? ['active'] : ['draft', 'reverted-draft']
      
      fetchInitialDsrData({
        dsrId, 
        dsrStatuses: requiredStatuses, 
        isLiveEdit: isLiveEdit || false,
        isViewArchived:false
      }) 
      initRef.current = true
    }
  }, [dsrId, fetchInitialDsrData, isLiveEdit])
  
  useEffect(() => {
    if (dsrInvalidRequestRedux === true) {
      history.push(`/invalid`, {
        redirectionUri: `/home`
      })
    }

  }, [currentDsrId, dsrId, dsrInvalidRequestRedux])

  useEffect(() => {
    if (isLockedRoom === true && !lockedRoomRef.current) {
      if (isLiveEdit === true) {
        setContentIsUnavailable(true)
      } else {
        setIsPagePreviewMode(true)
      }

      lockedRoomRef.current = true
    }

  }, [setIsPagePreviewMode, lockedRoomRef.current, isLockedRoom])


  useEffect(() => {
    if (selectedDisplaySubpage === 'updates-subpage' && ((dsrFeedContent.length === 0) || (currentDsrId !== dsrId))) getDsrFeedContent(dsrId)
  }, [currentDsrId, dsrFeedContent, dsrId, getDsrFeedContent, selectedDisplaySubpage])

  useEffect(() => {
    dsrPagesRedux && setDsrPagesLocalState(_.cloneDeep(dsrPagesRedux))
  }, [dsrPagesRedux])

  useEffect(() => {
    if (dsrPagesLocalState) setDsrHasUnsavedChanges(!_.isEqual(dsrPagesLocalState, dsrPagesRedux))
  }, [dsrPagesRedux, dsrPagesLocalState, setDsrHasUnsavedChanges])

  useEffect(() => {
    if (dsrId && dsrHasUnsavedChanges) {
      updateDsr({
        dsrId,
        // Note: Future scope - save only the parts that have changed, and not all the pages. Less risk of wiping out error data.
        dsrPages: dsrPagesLocalState,
        sourceEvent: 'save-draft-dsr',
        // Renew the lock time (if applicable)
        // updateLockTimer: true 
      })
    }


  }, [dsrId, dsrHasUnsavedChanges, dsrPagesLocalState, updateDsr])

  useEffect(() => {
    if (lockedRoomMetadata) {
      const { isLockerUser, noLockRequired } = lockedRoomMetadata || {}
      if (!!isLockerUser && noLockRequired !== true) {
        const handleBeforeUnload = event => {

          unlockRoom({
            dsrId,
            sourceEvent: 'unlock-room',
          });
        }

        window.addEventListener('beforeunload', handleBeforeUnload);

        // Proper cleanup function
        return () => {
          window.removeEventListener('beforeunload', handleBeforeUnload);
        };
      };
    }
  }, [lockedRoomMetadata]);

  useEffect(() => {
    if (lockedRoomMetadata) {
      const { isLockerUser, noLockRequired } = lockedRoomMetadata || {}
      if (!!isLockerUser && noLockRequired !== true) {
        const unlisten = history.listen(() => {
          unlockRoom({
            dsrId,
            sourceEvent: 'unlock-room',
          });
        });

        return () => {
          unlisten(); // Cleanup listener when component unmounts
        };
      };
    }
  }, [history, lockedRoomMetadata, dsrId]);

  // Set the timer
  const startUnlockTimer = () => {
    if (timeoutRef.current) { // ABC: Clear the previous timeout
      clearTimeout(timeoutRef.current); // ABC: Clear the previous timeout
    }

    timeoutRef.current = setTimeout(() => {
      unlockRoom({
        dsrId,
        sourceEvent: 'unlock-room',
      });
      handleIdleRoomUnlocked()
    }, 1000 * 60 * 60); // Timeout duration in milliseconds (1 hr)
  };

  const handleActivity = () => {
    // Reset the timer whenever an activity is detected
    startUnlockTimer();
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      startUnlockTimer(); // Reset the timer when the user returns to the page
    }
  };


  useEffect(() => {
    if (lockedRoomMetadata) {
      const { noLockRequired } = lockedRoomMetadata || {};
      if (noLockRequired !== true) {
        // Start the initial timer
        startUnlockTimer();

        // Cleanup: Clear timeout when component is unmounted or dependencies change
        return () => {
          if (timeoutRef.current) { // ABC: Added check and clear timeout on unmount
            clearTimeout(timeoutRef.current); // ABC: Clear timeout to prevent memory leak
          }
        };
      }
    }

  }, [lockedRoomMetadata]);

  useEffect(() => {
    if (lockedRoomMetadata) {
      const { noLockRequired } = lockedRoomMetadata || {};
      if (noLockRequired !== true) {
        const activityEvents = ['load', 'mousedown', 'touchstart', 'click', 'keypress', 'mousemove', 'keydown'];

        // Add event listeners for user activity
        activityEvents.forEach(eventType => {
          document.addEventListener(eventType, handleActivity);
        });

        // Listen for visibility change events
        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Cleanup function to remove event listeners and clear the timer when the component unmounts
        return () => {
          activityEvents.forEach(eventType => {
            document.removeEventListener(eventType, handleActivity);
          });
          document.removeEventListener('visibilitychange', handleVisibilityChange);

          // Clear the timeout if the component unmounts
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
        };
      }
    }
  }, [dsrId, lockedRoomMetadata]);

  const handleIdleRoomUnlocked = () => {
    openLaunchdeckModal('idle-room-unlocked')
  }

  const handleDsrModalClose = () => {
    closeLaunchdeckModal()
  }

  // Team Members Management
  const handleViewTeamMembers = () => {
    openLaunchdeckModal('view-team-members')
  }

  // DSR status Management
  const handleChangeUserActionRequired = (newUserActionRequired, modalTransferPayload) => {
    setModalTransferPayload(modalTransferPayload)
    openLaunchdeckModal(newUserActionRequired)
  }

  const handlerSetDsrPages = (pages) => {
    setDsrPagesLocalState(pages)
  }

  const handlerAddDsrPage = (newDsrPage, sourcePageId) => {
    if (!sourcePageId) {
      // Adds brand new page
      setDsrPagesLocalState((prevState) => [...prevState, newDsrPage])
    } else {
      // Puts duplicated page right below source page
      const sourcePageIndex = dsrPagesLocalState?.findIndex((page) => page.pageId === sourcePageId)
      if (sourcePageIndex >=0) {
        const newPagesArray = [
          ...dsrPagesLocalState.slice(0, sourcePageIndex + 1),
          newDsrPage, // Insert the new object
          ...dsrPagesLocalState.slice(sourcePageIndex + 1), 
        ];

        setDsrPagesLocalState(newPagesArray)
      }
    }
  }

  const handlerRemoveDsrPage = (dsrPageId) => {
    setDsrPagesLocalState((prevState) => prevState.filter((page) => page.pageId !== dsrPageId))
  }

  useEffect(() => {
    return () => resetDsrFeedContent() && resetDsrTitle() && resetDsrSelectedDisplaySubpage() && resetDsrPages() && updateDsrReset()
  }, [resetDsrFeedContent, resetDsrTitle, resetDsrSelectedDisplaySubpage, resetDsrPages, updateDsrReset])

  const renderUnavailableContent = () => {
    return (
      <Flex w='100%'
        justify='center'
        align='center'
        p='5em 10em'
      >
        <Flex w='100%' h='100%' justify='center' align='flex-start' >
          <Flex minW='25em' border='1px solid' borderColor='brandDark.500' rounded='0.5em' justify='center' align='center'>
            <Flex direction='column' justify='center' align='center' p='1em' minH='15em' minW={['10em', '10em', '50em', '50em']}>
              <Flex w='100%' justify='center' my='3em'>
                <Box as={TbDeviceDesktopQuestion} boxSize="5em" color="brandDark.500" />
              </Flex>

              <Box fontSize='0.875em'>This Room is currently unavailable, or you may have used an invalid link. </Box>

              {dsrId && <Flex mt='1em' mb='3em' justify='center' alignItems='center'>
                <LaunchdeckButton bg='brand.500' color='white' onClick={() =>redirectToNewPage(`/dsr/${dsrId}`)}
                  label='Return' />

              </Flex>}
            </Flex>
          </Flex>

        </Flex>
      </Flex>
    )
  }


  if (isMobileView) {
    return (
      <Flex margin='-2% -7% -7% -7%' height='100vh' justifyContent='center' alignItems='center' background='white' flexDirection='column'>
        <Flex mb='0.625em' px='2em' justify='center' textAlign='center'>
          For the best experience editing your content, we recommend using a computer's wider display
        </Flex>
        <Flex direction='column' mt='1em' justify='center' alignItems='center'>
          <Flex justify={['center', 'flex-end', 'flex-end', 'flex-end']} w='100%'>
            <LaunchdeckButton bg='brand.500' color='white' onClick={() => window.location.replace('/home')} label='Edit on a computer later' />
          </Flex>
        </Flex>
      </Flex>
    )
  }
  

  // UX Listener
  return (
    <Flex direction="column" height='100%'>
      <Flex direction="row" flexGrow="1">
        <Flex w="100%" direction="column" alignItems="center" bg="white" rounded="0.8em">
          <Box width='100%' position='fixed' zIndex='2'>
            {hideOptionForInvalidLink ?
              <Header className="app-layout__header" displayMode='' />
              :
              <DsrHeader
                handleViewTeamMembers={handleViewTeamMembers}
                dsrTeamMembers={dsrTeamMembersRedux}
                dsrId={dsrId}
                dsrPages={dsrPagesLocalState}
                isEditMode={true}
                isPagePreviewMode={isPagePreviewMode}
                setIsPagePreviewMode={setIsPagePreviewMode}
                isLiveEdit={isLiveEdit}
                hideOptionForInvalidLink={hideOptionForInvalidLink}
                handleChangeUserActionRequired={handleChangeUserActionRequired}
              />
            }
          </Box>

          {
            contentIsUnavailable ?
              <Flex w="100%" className="authentication-container" justify="center" marginTop='55px'>
                {renderUnavailableContent()}
              </Flex>
              :
              <Flex w="100%" className="authentication-container" justify="center" marginTop='140px'>
                {isPagePreviewMode
                  ? (
                    <RenderPreviewDsrContent
                      dsrId={dsrId}
                      pagesLocalState={dsrPagesLocalState}
                      dsrTitle={dsrTitle}
                    />
                  )
                  : (
                    <>
                    <Box flex='10'>
                     <RenderDraftDsrContent
                      pagesLocalState={dsrPagesLocalState}
                      replaceDsrPages={handlerSetDsrPages}
                      addDsrPage={handlerAddDsrPage}
                      removeDsrPage={handlerRemoveDsrPage}
                      duplicateDsrPage={duplicateDsrPage}
                      handleChangeUserActionRequired={handleChangeUserActionRequired}
                    />
                    </Box>
                           
                    </>
                  )
                }
                         
                <DsrGenericErrorModal dsrId={dsrId} />
                <DsrDynamicModal
                  dsrId={dsrId}
                  userActionRequired={userActionRequiredRedux}
                  showDsrModal={showDsrModalRedux}
                  showDsrModalLoader={showDsrModalLoaderRedux}
                  handleDsrModalClose={handleDsrModalClose}
                  dsrPages={dsrPagesLocalState}
                  handleChangeUserActionRequired={handleChangeUserActionRequired}
                  modalTransferPayload={modalTransferPayload}
                  // handleCreateNewFaqQuestion={handleCreateNewFaqQuestion}
                />
               
              </Flex>
          }
        </Flex>

      </Flex>
    </Flex>
  )
}
